import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Row, Segmented, Tag, Typography,
} from 'antd';
import { PaperClipOutlined } from '@ant-design/icons';
import Moment from 'moment';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import { IconoDeSeguimiento } from 'utils/iconosDeSeguimiento';

const { Text } = Typography;

const formatDate = (value) => (value ? Moment(value).format('DD/MM/YYYY') : '');

// Nombre de una persona con su plaza y área debajo.
export function PersonaConPlaza({ nombre, plaza, area }) {
  if (!nombre) return <Text type="secondary">—</Text>;
  return (
    <div>
      <Text strong>{nombre}</Text>
      {(plaza || area) && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {[plaza, area].filter(Boolean).join(' · ')}
          </Text>
        </div>
      )}
    </div>
  );
}

PersonaConPlaza.propTypes = {
  nombre: PropTypes.string,
  plaza: PropTypes.string,
  area: PropTypes.string,
};

PersonaConPlaza.defaultProps = { nombre: null, plaza: null, area: null };

// Ventana con los seguimientos (notas) de un registro: lo más importante de cada uno, separado
// en Activas y Cerradas. Agregar abre una nota nueva; Editar (o doble clic) abre la nota.
function ListaDeSeguimientos({
  open, titulo, subtitulo, notas, mutable, onAgregar, onEditar, onCerrar,
}) {
  const [filtro, setFiltro] = useState('activas');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const activas = notas.filter((n) => !n.se_cierra_la_nota);
  const cerradas = notas.filter((n) => n.se_cierra_la_nota);
  const visibles = (filtro === 'activas' ? activas : cerradas).map((n) => ({ ...n, key: n.id }));

  useEffect(() => {
    if (open) {
      setFiltro('activas');
      setSelectedRowKeys([]);
    }
  }, [open]);

  const editar = (record) => {
    const nota = notas.find((n) => n.id === (record?.id ?? selectedRowKeys[0]));
    if (nota) onEditar(nota);
  };

  const columns = [
    {
      titleText: 'Ícono',
      dataIndex: 'iconografia_icono',
      width: 70,
      render: (value, record) => (
        <span title={record.iconografia_descripcion}>
          <IconoDeSeguimiento name={value} style={{ fontSize: 18 }} />
        </span>
      ),
    },
    {
      titleText: 'Emisor',
      dataIndex: 'emisor_nombre',
      width: 230,
      render: (value, record) => (
        <PersonaConPlaza nombre={value} plaza={record.emisor_puesto} area={record.emisor_area} />
      ),
    },
    {
      titleText: 'Responsable',
      dataIndex: 'responsable_de_seguimiento_nombre',
      width: 240,
      render: (value, record) => (
        <PersonaConPlaza
          nombre={value}
          plaza={record.plaza_descripcion}
          area={record.area_descripcion}
        />
      ),
    },
    { titleText: 'Se Requiere', dataIndex: 'se_requiere', width: 220 },
    {
      titleText: 'Fecha Compromiso',
      dataIndex: 'fecha_compromiso',
      width: 130,
      render: formatDate,
    },
    {
      titleText: 'Adjuntos',
      dataIndex: 'adjuntos_count',
      width: 90,
      render: (value) => (value ? (
        <span>
          <PaperClipOutlined />
          {` ${value}`}
        </span>
      ) : '—'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales_descripcion',
      width: 110,
      render: (value, record) => (record.se_cierra_la_nota
        ? <Tag color="default">Cerrada</Tag>
        : <Tag color="blue">{value || 'Activa'}</Tag>),
    },
  ];

  // Permisos locales: la privacidad de las notas ya la resuelve el backend.
  const permission = {
    add: mutable, change: true, delete: false, hasAllViewPermission: true,
  };

  return (
    <Modal
      open={open}
      title={(
        <Row justify="space-between" align="middle" wrap={false}>
          <div>
            <Text className="primary-text" style={{ fontSize: 18 }}>{titulo}</Text>
            {subtitulo && (
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>{subtitulo}</Text>
              </div>
            )}
          </div>
          <FormSubmitControls onCancel={onCerrar} />
        </Row>
      )}
      onCancel={onCerrar}
      footer={null}
      closable={false}
      width="min(1250px, 92vw)"
      // Debajo de la ventana de la nota para que esta quede encima al abrirla.
      zIndex={900}
      destroyOnClose
    >
      <Segmented
        value={filtro}
        onChange={(value) => { setFiltro(value); setSelectedRowKeys([]); }}
        options={[
          { value: 'activas', label: `Activas (${activas.length})` },
          { value: 'cerradas', label: `Cerradas (${cerradas.length})` },
        ]}
        style={{ marginBottom: 12 }}
      />
      <Table
        cols={columns}
        data={visibles}
        rowSelection={{ selectedRowKeys, type: 'radio' }}
        handleOnRowClick={(record) => setSelectedRowKeys([record.id])}
        controls={{ onClickAdd: onAgregar, onClickEdit: (record) => editar(record) }}
        permission={permission}
        allowDelete={false}
        // Las notas cerradas solo se consultan: no se agregan nuevas desde esa pestaña.
        allowAdd={filtro === 'activas'}
        allowDoubleClick
        mobileColIndex={3}
      />
    </Modal>
  );
}

ListaDeSeguimientos.propTypes = {
  open: PropTypes.bool.isRequired,
  titulo: PropTypes.string.isRequired,
  subtitulo: PropTypes.string,
  notas: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mutable: PropTypes.bool,
  onAgregar: PropTypes.func.isRequired,
  onEditar: PropTypes.func.isRequired,
  onCerrar: PropTypes.func.isRequired,
};

ListaDeSeguimientos.defaultProps = { mutable: true, subtitulo: null };

export default ListaDeSeguimientos;
